import type { Config } from "tailwindcss";
import plugin from "tailwindcss/plugin";

const config = {
  darkMode: "selector",
  content: [
    "./pages/**/*.{ts,tsx}",
    "./components/**/*.{ts,tsx}",
    "./app/**/*.{ts,tsx}",
    "./src/**/*.{ts,tsx}",
  ],
  prefix: "",
  theme: {
    container: {
      center: true,
      screens: {
        '2xl': '96rem'
      },
    },
    colors: {
      // Raw color variables — use with caution, specifically when semantic color variables aren't appropriate
      transparent: "transparent",
      black: {
        DEFAULT: 'rgb(var(--black))',
        70: "rgb(var(--black) / 67%)",
        40: "rgb(var(--black) / 44%)",
        20: "rgb(var(--black) / 18%)",
        10: "rgb(var(--black) / 10%)",
        5: "rgb(var(--black) / 5%)",
      },
      white: {
        DEFAULT: "rgb(var(--white))",
        80: "rgb(var(--white) / 78%))",
        40: "rgb(var(--white) / 44%))",
        30: "rgb(var(--white) / 33%))",
        20: "rgb(var(--white) / 16%))",
        10: "rgb(var(--white) / 12%))",
        5: "rgb(var(--white) / 7%))",
      },
      gray: {
        900: "rgb(var(--gray-900))",
        "900-80": "rgb(var(--gray-900) / 80%)",
        850: "rgb(var(--gray-850))",
        800: "rgb(var(--gray-800))",
        700: "rgb(var(--gray-700))",
        600: "rgb(var(--gray-600))",
        "600-blended": "rgb(var(--gray-600-blended) / 69%)",
        500: "rgb(var(--gray-500))",
        400: "rgb(var(--gray-400))",
        "400-blended": "rgb(var(--gray-400-blended) / 51%)",
        300: "rgb(var(--gray-300))",
        "300-blended": "rgb(var(--gray-300-blended) / 32%;)",
        200: "rgb(var(--gray-200))",
        "200-blended": "rgb(var(--gray-200-blended) / 22%)",
        150: "rgb(var(--gray-150))",
        "150-blended": "rgb(var(--gray-150-blended) / 16%)",
        100: "rgb(var(--gray-100))",
        "100-blended": "rgb(var(--gray-100-blended) / 12%)",
        50: "rgb(var(--gray-50))",
        "50-70": "rgb(var(--gray-50) / 70%)",
        "50-blended": "rgb(var(--gray-50-blended) / 5%)",
      },
      orange: {
        700: "rgb(var(--orange-700))",
        600: "rgb(var(--orange-600))",
        500: "rgb(var(--orange-500))",
        400: "rgb(var(--orange-400))",
        300: "rgb(var(--orange-300))",
        200: "rgb(var(--orange-200))",
        100: "rgb(var(--orange-100))",
      },
      green: {
        800: "rgb(var(--green-800))",
        700: "rgb(var(--green-700))",
        "700-20": "rgb(var(--green-700) / 20%)",
        400: "rgb(var(--green-400))",
        200: "rgb(var(--green-200))",
      },
      red: {
        700: "rgb(var(--red-700))",
        500: "rgb(var(--red-500))",
        "500-20": "rgb(var(--red-500) / 20%)",
        300: "rgb(var(--red-300))",
        100: "rgb(var(--red-100))",
      },
      blue: {
        800: "rgb(var(--blue-800))",
        700: "rgb(var(--blue-700))",
        400: "rgb(var(--blue-400))",
        300: "rgb(var(--blue-300))",
        200: "rgb(var(--blue-200))",
      },

      // Semantic color variables
      background: 'var(--background)',
      "background-80": "var(--background-80)",
      foreground: 'var(--foreground)',
      surface1: {
        100: 'var(--surface1-100)',
        DEFAULT: 'var(--surface1)',
      },
      surface2: {
        100: 'var(--surface2-100)',
        DEFAULT: 'var(--surface2)',
      },
      surface3: {
        100: 'var(--surface3-100)',
        DEFAULT: 'var(--surface3)',
      },
      border1: 'var(--border1)',
      border2: 'var(--border2)',
      primary: 'var(--primary)',
      secondary: {
        100: 'var(--secondary-100)',
        DEFAULT: 'var(--secondary)',
      },
      tertiary: {
        100: 'var(--tertiary-100)',
        DEFAULT: 'var(--tertiary)',
      },
      link: {
        DEFAULT: 'var(--link)',
        hover: 'var(--link-hover)',
      },
      button: {
        primary: {
          DEFAULT: 'var(--button-primary)',
          hover: 'var(--button-primary-hover)',
          text: {
            DEFAULT: 'var(--button-primary-text)',
            hover: 'var(--button-primary-text-hover)',
          },
        },
        ghost: {
          hover: 'var(--button-ghost-hover)',
        },
        secondary: {
          DEFAULT: 'var(--button-secondary)',
          hover: 'var(--button-secondary-hover)',
          text: {
            DEFAULT: 'var(--button-secondary-text)',
            hover: 'var(--button-secondary-text-hover)',
          },
        },
        outline: {
          border: 'var(--button-outline-border)',
          hover: 'var(--button-outline-hover)',
          text: 'var(--button-outline-text)',
          "text-hover": 'var(--button-outline-text-hover)',
        },
      },
      input: {
        text: 'var(--input-text)',
        border: {
          DEFAULT: 'var(--input-border)',
          hover: 'var(--input-border-hover)',
          focus: 'var(--input-border-focus)',
        },
        placeholder: {
          DEFAULT: 'var(--input-placeholder)',
          hover: 'var(--input-placeholder-hover)',
          focus: 'var(--input-placeholder-focus)',
        },
      },
      accent: {
        primary: 'var(--accent-primary)',
      },
      danger: {
        DEFAULT: 'var(--danger)',
        text: {
          DEFAULT: 'var(--danger-text)',
          hover: 'var(--danger-text-hover)',
        },
      },
      success: {
        DEFAULT: 'var(--success)',
        text: 'var(--success-text)',
      },
      focus: 'var(--focus)',
      scrim: {
        DEFAULT: 'var(--scrim)',
        nonmodal: 'var(--scrim-nonmodal)',
      }
    },
    fontSize: {
      xs: [
        "12px",
        {
          lineHeight: "16px",
          letterSpacing: "0.01em",
        },
      ],
      sm: [
        "14px",
        {
          lineHeight: "20px",
          letterSpacing: "0.005em",
        },
      ],
      base: [
        "16px",
        {
          lineHeight: "24px",
          letterSpacing: "0em",
        },
      ],
      xl: [
        "21px",
        {
          lineHeight: "32px",
          letterSpacing: "-0.005em",
        },
      ],
      "3xl": [
        "28px",
        {
          lineHeight: "40px",
          letterSpacing: "0em",
        },
      ],
      "4xl": [
        "36px",
        {
          lineHeight: "48px",
          letterSpacing: "-0.0025em",
        },
      ],
      "5xl": [
        "48px",
        {
          lineHeight: "56px",
          letterSpacing: "-0.005em",
        },
      ],
      "6xl": [
        "64px",
        {
          lineHeight: "72px",
          letterSpacing: "-0.02em",
        },
      ],
    },
    data: {
      open: 'state~="open"',
      closed: 'state~="closed"',
    },
    extend: {
      screens: {
        "3xl": "1920px",
      },
      borderWidth: {
        "1": "1px",
        "3": "3px",
      },
      borderRadius: {
        lg: "var(--radius)",
        md: "calc(var(--radius) - 2px)",
        sm: "calc(var(--radius) - 4px)",
      },
      padding: {
        navbar: "var(--navbar-height)",
        'portfolio-width': "var(--portfolio-width)",
        "page-gutter": "var(--page-gutter)",
      },
      margin: {
        navbar: "var(--navbar-height)",
        'portfolio-width': "var(--portfolio-width)",
        "page-gutter": "var(--page-gutter)",
      },
      top: {
        navbar: "var(--navbar-height)",
      },
      fontFamily: {
        serif: ["var(--font-serif)", "serif"],
        "pp-editorial-new": ["var(--font-serif)", "serif"],
      },
      height: {
        navbar: "var(--navbar-height)",
      },
      minHeight: {
        "content-with-navbar": "calc(100vh - var(--navbar-height))",
      },
      minWidth: {
        '4xl': '60rem',
        'portfolio-width': "var(--portfolio-width)",
      },
      maxWidth: {
        '4xl': '60rem',
        'portfolio-width': "var(--portfolio-width)",
        '78': '342px'
      },
      boxShadow: {
        DEFAULT: '0 1px 4px 0 rgb(var(--black) / 5%)',
        'md': '0 4px 10px -2px rgb(var(--black) / 5%), 0 2px 4px -2px rgb(var(--black) / 5%)',
        'lg': '0 10px 20px -4px rgb(var(--black) / 5%), 0 4px 8px -4px rgb(var(--black) / 5%)',
        'xl': '0 20px 28px -8px rgb(var(--black) / 10%), 0 8px 12px -8px rgb(var(--black) / 5%)',
        '2xl': '0 24px 64px -12px rgb(var(--black) / 18%)',
      },
      keyframes: {
        "accordion-down": {
          from: { height: "0" },
          to: { height: "var(--radix-accordion-content-height)" },
        },
        "accordion-up": {
          from: { height: "var(--radix-accordion-content-height)" },
          to: { height: "0" },
        },
        progress: {
          "0%": { "stroke-dashoffset": "var(--progress-start)" },
          "100%": { "stroke-dashoffset": "var(--progress-end)" },
        },
        enter: {
          "0%": {
            opacity: "0",
          },
          "100%": {
            opacity: "1",
          },
        },
        exit: {
          "0%": {
            opacity: "1",
          },
          "100%": {
            opacity: "0",
          },
        },
        "pulse-opacity": {
          "0%": { opacity: "1" },
          "50%": { opacity: "0.5" },
          "100%": { opacity: "1" },
        },
        "enter-scale": {
          "0%": {
            scale: "0.9",
            opacity: "0",
          },
          "100%": {
            scale: "1",
            opacity: "1",
          },
        },
        "exit-scale": {
          "0%": {
            scale: "1",
            opacity: "1",
          },
          "100%": {
            scale: "0.5",
            opacity: "0",
          },
        },
        marquee: {
          "0%": { transform: "translateZ(0)" },
          "100%": {
            transform:
              "translate3d(calc((var(--marquee-element-size) * var(--marquee-elements) + var(--marquee-elements-gap) * var(--marquee-elements-m1)) * -1 - var(--marquee-elements-gap)),0,0)",
          },
        },
        'caret-blink': {
          '0%': { opacity: '1' },
          '50%': { opacity: '0' },
          '100%': { opacity: '1' },
        },
        'shake': {
          '0%, 100%': { transform: 'translateX(0)' },
          '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-4px)' },
          '20%, 40%, 60%, 80%': { transform: 'translateX(4px)' },
        },
      },
      animation: {
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
        'spin-slow': 'spin 1.5s ease-in-out infinite',
        'fade-in': 'fadeIn 0.25s ease-in-out',
        progress: 'progress 10s ease-in-out',
        enter: 'enter 500ms ease-in-out',
        exit: 'exit 500ms ease-in-out',
        'pulse-opacity': 'pulse-opacity 1000ms ease-in-out infinite',
        'enter-scale': 'enter-scale 150ms ease-in-out',
        'exit-scale': 'exit-scale 150ms ease-in-out',
        marquee: 'marquee 20s linear infinite',
        'caret-blink': 'caret-blink 1s ease-in-out infinite',
        'shake': 'shake 0.5s ease-in-out',
      },
      transitionTimingFunction: {
        'default': 'cubic-bezier(0.34, 0.94, 0.1, 1)',
        'in-out-back': 'cubic-bezier(0.7, -0.4, 0.4, 1.4)',
      },
      transitionProperty: {
        height: "height",
        width: "width",
      },
      rotate: {
        22: "22deg",
      },
      spacing: {
        "6.5": "1.625rem",
        "22": "5.5rem",
        "26": "6.5rem",
        "trade-flow-line-offset": "calc(50% + 10px)",
        "trade-flow-line-height": "10.7rem",
      },
    },
  },
  safelist: [
    // Heights
    'h-8', 'h-12', 'h-14', 'sm:h-8', 'sm:h-12', 'sm:h-14', 'md:h-8', 'md:h-12', 'md:h-14', 'lg:h-8', 'lg:h-12', 'lg:h-14', 'xl:h-8', 'xl:h-12', 'xl:h-14', '2xl:h-8', '2xl:h-12', '2xl:h-14',
    // Padding
    'px-3', 'px-4', 'p-5', 'py-1.5', 'py-3', 'sm:px-3', 'sm:px-4', 'sm:p-5', 'sm:py-1.5', 'sm:py-3', 'md:px-3', 'md:px-4', 'md:p-5', 'md:py-1.5', 'md:py-3', 'lg:px-3', 'lg:px-4', 'lg:p-5', 'lg:py-1.5', 'lg:py-3', 'xl:px-3', 'xl:px-4', 'xl:p-5', 'xl:py-1.5', 'xl:py-3', '2xl:px-3', '2xl:px-4', '2xl:p-5', '2xl:py-1.5', '2xl:py-3',
    // Gap
    'gap-2', 'gap-3', 'sm:gap-2', 'sm:gap-3', 'md:gap-2', 'md:gap-3', 'lg:gap-2', 'lg:gap-3', 'xl:gap-2', 'xl:gap-3', '2xl:gap-2', '2xl:gap-3',
    // Text size
    'text-sm', 'text-base', 'text-xl', 'sm:text-sm', 'sm:text-base', 'sm:text-xl', 'md:text-sm', 'md:text-base', 'md:text-xl', 'lg:text-sm', 'lg:text-base', 'lg:text-xl', 'xl:text-sm', 'xl:text-base', 'xl:text-xl', '2xl:text-sm', '2xl:text-base', '2xl:text-xl',
    // Danger text hover for max balance states
    'text-danger-text-hover', 'group-hover:text-danger-text-hover',
  ],
  plugins: [
    require("tailwindcss-animate"),
    require("@tailwindcss/container-queries"),
    plugin(function ({ addVariant }) {
      addVariant('open', '[data-state="open"] &')
      addVariant('closed', '[data-state="closed"] &')
    })
  ],
} satisfies Config;

export default config;
